/**
 * Which rows an export means.
 *
 * The export button has always taken whatever the filters left, silently. That is the
 * right default — somebody who narrowed a register to twelve rows expects twelve rows in
 * the file — but it is only a default, and it was being applied as a decision. The other
 * want is just as real: filter the list to find your bearings, then take the whole thing
 * away. So when the page is showing less than it holds, the export asks first.
 *
 * Three sets, because there are three answers somebody might mean:
 *
 * - **selected** — the rows ticked on the table, when the table offers ticking.
 * - **filtered** — what is on screen: the tick-box filters, the search box and the
 *   column funnels together. One set, because to the person exporting they are one act.
 * - **all** — the collection, before any of it.
 *
 * Nothing here asks anything. It works out whether there is a question worth asking and
 * what the answers would be; the dialog is in `ExportButton`.
 */

export type ExportScopeId = 'selected' | 'filtered' | 'all'

export type ExportSets<T> = {
  selected: T[]
  filtered: T[]
  all: T[]
}

/** Fills in the sets a page did not give. A page with no filters has all three the same. */
export function exportSets<T>(filtered: T[], all?: T[], selected?: T[]): ExportSets<T> {
  return { selected: selected ?? [], filtered, all: all ?? filtered }
}

/**
 * Whether the export has anything to ask about.
 *
 * Not "is a filter switched on" — a filter that excludes nothing leaves the two answers
 * identical, and asking which of two identical things you meant is noise. The question is
 * whether the sets actually differ.
 */
export function needsExportChoice<T>(sets: ExportSets<T>): boolean {
  return sets.selected.length > 0 || sets.all.length > sets.filtered.length
}

/** Ticked rows win when there are any: ticking is the most deliberate act of the three. */
export function defaultExportScope<T>(sets: ExportSets<T>): ExportScopeId {
  if (sets.selected.length > 0) return 'selected'
  // With nothing held back, "filtered" and "all" are the same rows, and calling it
  // filtered would name the file as a slice of itself.
  return sets.all.length > sets.filtered.length ? 'filtered' : 'all'
}

export function rowsForScope<T>(sets: ExportSets<T>, scope: ExportScopeId): T[] {
  return scope === 'selected' ? sets.selected : scope === 'all' ? sets.all : sets.filtered
}

export type ExportOption = { id: ExportScopeId; label: string; count: number }

/**
 * The choices to put in front of somebody, in the order they read.
 *
 * Selected is offered only when something is ticked — an empty option that exports nothing
 * is a trap, not a choice.
 */
export function exportOptions<T>(sets: ExportSets<T>): ExportOption[] {
  const options: ExportOption[] = []
  if (sets.selected.length > 0) {
    options.push({ id: 'selected', label: 'Selected rows', count: sets.selected.length })
  }
  options.push({ id: 'filtered', label: 'Filtered rows', count: sets.filtered.length })
  options.push({ id: 'all', label: 'All rows', count: sets.all.length })
  return options
}

/**
 * What the file gets called.
 *
 * Three exports of the same register otherwise land in Downloads with the same name and a
 * (1) and a (2) after it, and no way to tell which was the whole thing. The scope goes in
 * the name for anything narrower than everything.
 */
export function scopedLabel(label: string, scope: ExportScopeId): string {
  return scope === 'all' ? label : `${label} ${scope}`
}
